import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { LogOut, Inbox, RefreshCw } from "lucide-react";

export default function AdminDashboard() {
  const { user, setUser, api } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios
      .get(`${api}/enquiries`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    else if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const logout = async () => {
    await axios.post(`${api}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <p className="text-muted-foreground text-sm">Checking session…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-secondary/30 pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-accent">Administration</p>
            <h1 className="mt-2 font-serif text-4xl text-navy">Student Enquiries</h1>
            <p className="mt-1 text-sm text-muted-foreground">Signed in as {user.email}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="refresh-enquiries-btn"
              className="inline-flex items-center gap-2 border border-border bg-background px-4 py-2.5 rounded-sm text-sm text-navy hover:border-accent transition-colors duration-300"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-navy text-primary-foreground px-4 py-2.5 rounded-sm text-sm hover:bg-accent transition-colors duration-300"
            >
              <LogOut size={15} /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-10 bg-background border border-border rounded-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
            <Inbox size={16} className="text-accent" />
            <p className="text-sm text-navy font-medium" data-testid="enquiries-count">
              {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} received
            </p>
          </div>
          {loading ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">Loading…</p>
          ) : enquiries.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground" data-testid="enquiries-empty">
              No enquiries yet. Submissions from the contact form will appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="border-b border-border text-left bg-secondary/40">
                    {["Received", "Name", "Email", "Phone", "City", "Program", "Message"].map((h) => (
                      <th key={h} className="px-6 py-3.5 text-xs tracking-[0.18em] uppercase text-muted-foreground font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors duration-200 align-top">
                      <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
                      </td>
                      <td className="px-6 py-4 font-medium text-navy whitespace-nowrap">{`${e.first_name} ${e.last_name}`.trim() || "—"}</td>
                      <td className="px-6 py-4 text-muted-foreground">{e.email}</td>
                      <td className="px-6 py-4 text-muted-foreground">{e.phone || "—"}</td>
                      <td className="px-6 py-4 text-muted-foreground">{e.city || "—"}</td>
                      <td className="px-6 py-4 text-navy">{e.program || "General"}</td>
                      <td className="px-6 py-4 text-muted-foreground max-w-xs">{e.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
